import { router } from "@inertiajs/react";
import {
  type CellTemplateProp,
  type ColumnRegular,
  type HyperFunc,
  RevoGrid,
  Template,
  type VNode,
  defineCustomElements,
} from "@revolist/react-datagrid";
import currency from "currency.js";
import { Package } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { ActionsCell } from "@/components/revogrid-cells/ActionsCell";
import { MatchSelectorCell } from "@/components/revogrid-cells/MatchSelectorCell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RevoGridProvider } from "@/contexts/revogrid-context";
import {
  formatCurrency,
  parseCurrencyInput,
} from "@/helpers/currency-formatter";
import { computeRevoGridHeight } from "@/helpers/revogrid";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useRevoGridEdit } from "@/hooks/use-revogrid-edit";
import type { Quote, QuoteItem, RequestItem } from "@/types";

// Initialize custom elements for RevoGrid
defineCustomElements();

/**
 * Props interface for the QuoteItemsGrid component
 */
interface QuoteItemsGridProps {
  /** Quote items to display in the grid */
  quoteItems: (QuoteItem & {
    unit_price_in_ars: number;
    product?: {
      id: number;
      normalized_name: string;
    };
    request_item?: {
      id: number;
      description: string;
      item_number: number;
    };
  })[];
  /** Quote information (used for currency formatting and edit URLs) */
  quote: Quote;
  /** Available request items for matching */
  availableRequestItems?: RequestItem[];
  /** Callback when bulk selection changes (for parent to show/hide bulk actions) */
  onBulkSelectionChange?: (selectedIds: number[]) => void;
}

/**
 * QuoteItemsGrid - A comprehensive RevoGrid-based table for managing quote items
 *
 * Features:
 * - Inline editing of quote item fields
 * - Bulk selection with multi-assignment capabilities
 * - Product matching with request items
 * - Currency formatting (USD/ARS with exchange rates)
 * - Responsive column sizing and auto-height
 */
export default function QuoteItemsGrid({
  quoteItems,
  quote,
  availableRequestItems = [],
  onBulkSelectionChange,
}: QuoteItemsGridProps) {
  const routes = useOrganizationRoutes();

  // Selection state management
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [allSelectedStatus, setAllSelectedStatus] = useState<number>(0); // 0-unchecked, 1-indeterminate, 2-checked
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedRequestItem, setSelectedRequestItem] =
    useState<AsyncOption | null>(null);

  // Grid reference for external control
  const gridRef = useRef<HTMLRevoGridElement>(null);

  const matchedRequestItemIds = new Set(
    quoteItems
      .map((item) => item.request_item_id)
      .filter((id): id is number => typeof id === "number" && id > 0),
  );

  // Transform request items to select options format
  // Transform quote items data for RevoGrid (handle null request_item_id)
  const transformedQuoteItems = quoteItems.map((item) => ({
    ...item,
    request_item_id: item.request_item_id ?? 0,
  }));

  // Calculate optimal grid height to prevent internal scrolling
  const gridHeight = computeRevoGridHeight(transformedQuoteItems.length);

  // Bulk selection handlers
  const selectAll = useCallback(
    (checked: boolean) => {
      const newSelection = new Set(
        checked ? transformedQuoteItems.map((row) => row.id) : [],
      );
      setSelected(newSelection);
    },
    [transformedQuoteItems],
  );

  const selectSingle = useCallback((rowId: number, checked: boolean) => {
    setSelected((prevSelected) => {
      const updatedSelected = new Set(prevSelected);
      if (checked) {
        updatedSelected.add(rowId);
      } else {
        updatedSelected.delete(rowId);
      }
      return updatedSelected;
    });
  }, []);

  // Update "Select All" checkbox state based on current selection
  useEffect(() => {
    if (selected.size === 0) {
      setAllSelectedStatus(0);
    } else if (selected.size === transformedQuoteItems.length) {
      setAllSelectedStatus(2);
    } else {
      setAllSelectedStatus(1);
    }
  }, [selected, transformedQuoteItems.length]);

  // Notify parent when bulk selection changes
  useEffect(() => {
    onBulkSelectionChange?.(Array.from(selected));
  }, [selected, onBulkSelectionChange]);

  // Request items loader for bulk assignment dialog
  const loadRequestItemOptions = useCallback(
    (term: string): AsyncOption[] => {
      const opts = availableRequestItems.map((ri) => ({
        value: ri.id,
        label: `${ri.item_number ? `#${ri.item_number} - ` : ""}${ri.description}`,
      }));
      const q = term.trim().toLowerCase();
      if (!q) return opts.slice(0, 50);
      return opts.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 50);
    },
    [availableRequestItems],
  );

  // Handle bulk assignment of request items
  const handleBulkAssign = useCallback(() => {
    if (!selectedRequestItem || selected.size === 0) return;

    const ids = Array.from(selected);
    router.post(
      routes.quoteItems.bulkAssignRequestItem(),
      {
        ids,
        request_item_id: selectedRequestItem.value,
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setBulkDialogOpen(false);
          setSelected(new Set());
          setSelectedRequestItem(null);
        },
      },
    );
  }, [selected, selectedRequestItem, routes]);

  // RevoGrid column definitions with inline editing capabilities
  const columns: ColumnRegular[] = [
    // Selection checkbox column
    {
      prop: "__selector",
      name: "",
      size: 42,
      minSize: 42,
      maxSize: 48,
      pin: "colPinStart",
      columnTemplate: (createElement: HyperFunc<VNode>) => {
        const status = allSelectedStatus;
        return createElement(
          "div",
          { className: "flex items-center justify-center h-full" },
          [
            createElement("input", {
              type: "checkbox",
              indeterminate: status === 1,
              checked: status > 1 || undefined,
              onChange(e: InputEvent) {
                if (e.target instanceof HTMLInputElement) {
                  selectAll(e.target.checked);
                }
              },
            }),
          ],
        );
      },
      cellTemplate: (
        createElement: HyperFunc<VNode>,
        { model, rowIndex }: CellTemplateProp,
      ) => {
        const m = model as QuoteItem;
        return createElement(
          "div",
          { className: "flex items-center justify-center h-full" },
          [
            createElement("input", {
              type: "checkbox",
              key: rowIndex,
              checked: selected.has(m.id) || undefined,
              onChange(e: InputEvent) {
                if (e.target instanceof HTMLInputElement) {
                  selectSingle(m.id, e.target.checked);
                }
              },
            }),
          ],
        );
      },
    },

    // Supplier product name - editable text field
    {
      prop: "supplier_product_name",
      name: "Producto del Proveedor",
      editor: "text",
      size: 325,
      minSize: 220,
      autoSize: true,
    },

    // Quantity - editable number field
    {
      prop: "quantity",
      name: "Cantidad",
      editor: "number",
      size: 90,
      minSize: 80,
    },

    // Unit price with dual currency display (USD/ARS)
    {
      prop: "unit_price",
      name: "Precio Unitario",
      editor: "number",
      size: 180,
      minSize: 140,
      cellTemplate: (
        createElement: HyperFunc<VNode>,
        cellProps: CellTemplateProp,
      ) => {
        const formatted = formatCurrency(
          Number(cellProps.value ?? 0),
          quote.currency,
        );
        const isUSD = quote.currency === "USD";
        const model = cellProps.model as QuoteItem;
        const arsEq =
          isUSD && model?.unit_price_in_ars != null
            ? formatCurrency(Number(model.unit_price_in_ars), "ARS")
            : null;

        const display = isUSD
          ? arsEq
            ? `${formatted} USD\n(${arsEq} ARS)`
            : `${formatted} USD`
          : formatted;

        return createElement(
          "div",
          {
            className:
              "text-right flex items-center h-full px-2 whitespace-pre-line text-sm",
          },
          display,
        );
      },
    },

    // Discount percentage - editable number field
    {
      prop: "discount_percentage",
      name: "Descuento (%)",
      editor: "number",
      size: 100,
      minSize: 90,
    },

    // Total price - calculated field (read-only)
    {
      prop: "total_price",
      name: "Total",
      readonly: true,
      size: 180,
      minSize: 140,
      cellTemplate: (
        createElement: HyperFunc<VNode>,
        cellProps: CellTemplateProp,
      ) => {
        const formatted = formatCurrency(
          Number(cellProps.value ?? 0),
          quote.currency,
        );
        const isUSD = quote.currency === "USD";

        // Calculate ARS total if we have unit_price_in_ars
        let arsTotal: string | null = null;
        if (
          isUSD &&
          (cellProps.model as QuoteItem)?.unit_price_in_ars != null &&
          (cellProps.model as QuoteItem)?.quantity != null
        ) {
          const unitArs = Number(
            (cellProps.model as QuoteItem).unit_price_in_ars,
          );
          const qty = Number((cellProps.model as QuoteItem).quantity);
          const totalArs = currency(unitArs, { precision: 2 }).multiply(
            qty,
          ).value;
          arsTotal = formatCurrency(totalArs, "ARS");
        }

        const display = isUSD
          ? arsTotal
            ? `${formatted} USD\n(${arsTotal} ARS)`
            : `${formatted} USD`
          : formatted;

        return createElement(
          "div",
          {
            className:
              "text-right font-medium flex items-center h-full px-2 whitespace-pre-line text-sm",
          },
          display,
        );
      },
    },

    // Request item matching - select dropdown
    {
      prop: "request_item_id",
      name: "Match con Solicitud",
      readonly: true,
      cellTemplate: Template(MatchSelectorCell, {
        availableRequestItems,
        matchedRequestItemIds,
        organizationSlug: routes.organizationSlug,
      }),
      size: 300,
      minSize: 220,
      pin: "colPinEnd",
    },

    // Actions column - React component
    {
      prop: "actions",
      name: "",
      readonly: true,
      cellTemplate: Template(ActionsCell, {
        organizationSlug: routes.organizationSlug,
      }),
      size: 60,
      pin: "colPinEnd",
      minSize: 60,
      maxSize: 60,
    },
  ];

  // Handle cell editing with custom value processing
  useRevoGridEdit<QuoteItem>({
    gridRef,
    pathGenerator: (item) => routes.quoteItems.show(item.id),
    resourceKey: "quote_item",
    valueProcessor: (prop, newValue) => {
      // Process numeric fields with proper currency parsing
      const numericFields = ["quantity", "unit_price", "discount_percentage"];
      if (numericFields.includes(String(prop))) {
        try {
          return parseCurrencyInput(String(newValue), quote.currency);
        } catch {
          console.warn(`Invalid numeric value for ${String(prop)}:`, newValue);
          return null; // Skip update on invalid input
        }
      }

      // Handle request item assignment (0 = unassigned)
      if (prop === "request_item_id") {
        return newValue === 0 || newValue === "0" || newValue === undefined
          ? null
          : Number(newValue);
      }

      return newValue as string | number | null;
    },
  });

  // Render empty state if no items
  if (transformedQuoteItems.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
        <p className="mb-2">No hay ítems en esta cotización</p>
        {availableRequestItems.length > 0 ? (
          <p className="text-sm">
            Haz clic en &quot;Agregar ítem&quot; para comenzar
          </p>
        ) : (
          <p className="text-sm">
            No hay ítems disponibles en la solicitud de compra para agregar
          </p>
        )}
      </div>
    );
  }

  return (
    <RevoGridProvider organizationSlug={routes.organizationSlug}>
      <div className="min-h-[300px]">
        {/* Bulk selection toolbar */}
        {selected.size >= 2 && (
          <div className="flex items-center gap-2 mb-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setBulkDialogOpen(true)}
            >
              Asignación múltiple ({selected.size})
            </Button>
            <span className="text-xs text-muted-foreground">
              Empareja varios ítems cotizados con una misma línea de la
              solicitud
            </span>
          </div>
        )}

        {/* RevoGrid table */}
        <RevoGrid
          ref={gridRef}
          source={transformedQuoteItems}
          columns={columns}
          theme="material"
          hideAttribution
          stretch={true}
          resize={true}
          // Enable auto-sizing for text overflow
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
          autoSizeColumn={{ mode: "autoSizeOnTextOverlap" as any }}
          // Set fixed height to fit all rows without internal scrolling
          style={{ height: `${gridHeight}px` }}
        />

        {/* Bulk assignment dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Asignación múltiple a Solicitud</DialogTitle>
              <DialogDescription>
                Elige un ítem de la solicitud para aplicar a {selected.size}{" "}
                ítems seleccionados.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <AsyncCombobox
                value={selectedRequestItem}
                onChange={setSelectedRequestItem}
                loadOptions={loadRequestItemOptions}
                placeholder="Buscar ítem de la solicitud…"
                buttonClassName="w-full"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBulkDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleBulkAssign}
                  disabled={!selectedRequestItem}
                >
                  Asignar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </RevoGridProvider>
  );
}
